import { useState, useId } from 'react';
import { Brain, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { SectionLabel } from '@/components/ui/section-label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useLabeling } from '../../context/useLabeling';
import { CheckIcon } from './LabelPanelIcons';
import type { ImageLabel } from '../../types/LabelingTypes';
import { LABEL_BORDER, LABEL_FILL, LABEL_STRIP, LABEL_TEXT } from '../../utils/labelStateClasses';

interface StateConfig {
  state: ImageLabel;
  shortcut: string;
  /** Full literal class strings so Tailwind can statically pick the tokens up. */
  active: string;
  dot: string;
  /** Secondary choice: spans the row below the two wear verdicts, in a single line. */
  compact?: boolean;
}

const STATE_CONFIGS: StateConfig[] = [
  {
    state: 'ok',
    shortcut: '1',
    active: 'bg-ok-soft border-ok-ring text-ok',
    dot: 'bg-ok',
  },
  {
    state: 'worn',
    shortcut: '2',
    active: 'bg-worn-soft border-worn-ring text-worn',
    dot: 'bg-worn',
  },
  {
    state: 'unreadable',
    shortcut: '3',
    active: 'bg-unreadable-soft border-unreadable-ring text-unreadable',
    dot: 'bg-unreadable',
    compact: true,
  },
];

interface StateButtonProps {
  config: StateConfig;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function StateButton({ config, label, isActive, onClick }: StateButtonProps) {
  const { shortcut, active, dot, compact } = config;
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onClick}
      data-testid={`state-button-${config.state}`}
      className={cn(
        'flex items-center gap-2 rounded-xl border text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
        compact ? 'col-span-2 flex-row justify-center px-5 py-3' : 'flex-col p-5',
        isActive ? active : 'bg-card border-border text-foreground hover:bg-muted'
      )}
    >
      <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', dot)} />
      <span>{label}</span>
      <kbd className="text-2xs font-mono bg-muted/60 px-1.5 py-0.5 rounded border border-border/50">
        {shortcut}
      </kbd>
      {isActive && <CheckIcon />}
    </button>
  );
}

interface AutoAdvanceToggleProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function AutoAdvanceToggle({ label, checked, onChange }: AutoAdvanceToggleProps) {
  const id = useId();
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
      <label htmlFor={id} className="text-sm text-muted-foreground cursor-pointer select-none">
        {label}
      </label>
      <Switch id={id} aria-label={label} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function LabelPanel() {
  const { t } = useTranslation();
  const {
    current,
    filtered,
    history,
    autoAdvance,
    applyLabel,
    classifyWithAi,
    classifyAllWithAi,
    acceptSuggestion,
    rejectSuggestion,
    acceptAllSuggestions,
    rejectAllSuggestions,
    setAutoAdvance,
    goPrev,
    goNext,
    undo,
  } = useLabeling();

  const [classifying, setClassifying] = useState(false);
  const [classifyingAll, setClassifyingAll] = useState(false);

  if (!current) return null;

  const currentIndex = filtered.findIndex((i) => i.key === current.key);
  const pendingCount = filtered.filter((i) => i.cnn !== null).length;

  const stateLabelMap: Record<ImageLabel, string> = {
    ok: t('labeling.state.ok'),
    worn: t('labeling.state.worn'),
    unreadable: t('labeling.state.unreadable'),
  };

  const handleClassify = async () => {
    if (classifying) return;
    setClassifying(true);
    try {
      await classifyWithAi(current.key);
    } finally {
      setClassifying(false);
    }
  };

  const handleClassifyAll = async () => {
    if (classifyingAll) return;
    setClassifyingAll(true);
    try {
      await classifyAllWithAi();
    } finally {
      setClassifyingAll(false);
    }
  };

  const cnn = current.cnn;
  const cnnPct = cnn?.confidence !== undefined ? Math.round(cnn.confidence * 100) : null;

  return (
    <div className="flex min-h-0 flex-col overflow-y-auto p-4" data-testid="label-panel">
      <SectionLabel className="mb-3">{t('labeling.panel.stateHead')}</SectionLabel>

      <div className="grid grid-cols-2 gap-3">
        {STATE_CONFIGS.map((cfg) => (
          <StateButton
            key={cfg.state}
            config={cfg}
            label={stateLabelMap[cfg.state]}
            isActive={current.label === cfg.state}
            onClick={() => applyLabel(current.key, cfg.state)}
          />
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-border flex flex-col gap-2">
        {/* Classify row: single + all */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            disabled={classifyingAll}
            loading={classifying}
            onClick={handleClassify}
          >
            {!classifying && <Brain aria-hidden />}
            {t('labeling.cnn.button')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={classifying}
            loading={classifyingAll}
            onClick={handleClassifyAll}
            title={t('labeling.cnn.classifyAll')}
            className="shrink-0"
          >
            {t('labeling.cnn.classifyAll')}
          </Button>
        </div>

        {/* CNN suggestion strip for current image */}
        {cnn && (
          <div className={cn('rounded-lg border overflow-hidden', LABEL_BORDER[cnn.mappedState])}>
            <div className={cn('flex flex-col gap-1.5 py-2 pr-2 pl-3', LABEL_STRIP[cnn.mappedState])}>
              {/* Row 1: badge + label + accept/reject */}
              <div className="flex items-center gap-1.5">
                <Badge tone="primary" size="sm" caps className="font-mono">
                  {t('labeling.cnn.tag')}
                </Badge>
                <span
                  className={cn('flex-1 text-xs font-semibold truncate', LABEL_TEXT[cnn.mappedState])}
                >
                  {stateLabelMap[cnn.mappedState]}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <IconButton
                    icon={Check}
                    variant="outline"
                    label={t('labeling.cnn.accept')}
                    onClick={() => acceptSuggestion(current.key)}
                  />
                  <IconButton
                    icon={X}
                    label={t('labeling.cnn.reject')}
                    onClick={() => rejectSuggestion(current.key)}
                  />
                </div>
              </div>

              {/* Row 2: confidence bar or no-score */}
              {cnnPct !== null ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-[3px] rounded-full bg-border overflow-hidden">
                    <div
                      className={cn('h-full rounded-full', LABEL_FILL[cnn.mappedState])}
                      style={{ width: `${cnnPct}%` }}
                    />
                  </div>
                  <span className="text-2xs font-mono text-muted-foreground shrink-0">
                    {cnnPct}%
                  </span>
                </div>
              ) : (
                <span className="text-2xs font-mono text-muted-foreground">
                  {t('labeling.cnn.noScore')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Pending bulk row */}
        {pendingCount > 0 && (
          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-muted/50 border border-border">
            <span className="flex-1 text-2xs font-mono text-muted-foreground">
              {t('labeling.cnn.pendingCount', { count: pendingCount })}
            </span>
            <Button variant="outline" size="sm" onClick={acceptAllSuggestions}>
              {t('labeling.cnn.acceptAll')}
            </Button>
            <Button variant="ghost" size="sm" onClick={rejectAllSuggestions}>
              {t('labeling.cnn.rejectAll')}
            </Button>
          </div>
        )}
      </div>

      <AutoAdvanceToggle
        label={t('labeling.toolbar.autoAdvance')}
        checked={autoAdvance}
        onChange={setAutoAdvance}
      />

      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" size="sm" disabled={currentIndex === 0} onClick={goPrev}>
            {t('labeling.panel.prev')}
          </Button>
          <Button variant="outline" size="sm" disabled={history.length === 0} onClick={undo}>
            {t('labeling.panel.undo')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={currentIndex === filtered.length - 1}
            onClick={goNext}
          >
            {t('labeling.panel.next')}
          </Button>
        </div>
      </div>
    </div>
  );
}
